import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  OnDestroy,
  signal,
  viewChild,
} from "@angular/core";

import { ICellRendererAngularComp } from "ag-grid-angular";
import {
  CellValueChangedEvent,
  Column,
  ColumnPinnedEvent,
  ColumnResizedEvent,
  ColumnVisibleEvent,
  ICellRendererParams,
} from "ag-grid-enterprise";

import { AutoOpenMenuComponent } from "@components/index";
import { BaseActionsCellRenderer } from "@shared/cell-renderers/base-actions.cell-renderer";
import { CommonConstants } from "@shared/constants";
import { CellRendererUtils, CommonUtils } from "@shared/utils";

interface ChipBounds {
  width: number;
  left: number;
  right: number;
}

@Component({
  standalone: false,
  selector: "app-chips-cell-renderer",
  templateUrl: "./chips.cell-renderer.html",
  styleUrls: ["./shared-styles.scss", "./chips.cell-renderer.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChipsCellRendererComponent
  extends BaseActionsCellRenderer
  implements ICellRendererAngularComp, OnDestroy
{
  public readonly MAX_CHIPS_TEXT_LENGTH_TO_SHOW = CommonConstants.MAX_CHIPS_TEXT_LENGTH_TO_SHOW;

  public readonly DEFAULT_CELL_CONTAINER_CLASS = CellRendererUtils.cellContainerClass;

  public hasLink = false;

  private isFirstRender = true;

  private columnResizedListener: (() => void) | null = null;

  public cellContainerClass = signal<string>(this.DEFAULT_CELL_CONTAINER_CLASS);

  public cellContainer = viewChild<ElementRef>("cellContainer");

  public readonly tagClass = "app-tag";

  originalValue = signal([]);

  originalCellContainerWidth = signal<number>(null);

  chipBounds = signal<ChipBounds[]>([]);

  chipsGap = signal<number>(0);

  xOffsetBetweenCellContainerAndParent = signal<number>(null);

  hiddenChipsNumber = computed(() => this.hiddenChips().length);

  hiddenChips = computed(() => this.originalValue().slice(this.params().value.length));

  warningTooltipText = computed<string>(() => this.params()["warningTooltipText"]);

  autoOpenMenu = viewChild<AutoOpenMenuComponent>("autoOpenMenu");

  onChipLinkClick = (chip: any): void => {
    console.log(chip);
  };

  agInit(params: ICellRendererParams): void {
    this.initializeParams(params);
    this.setupColumnResizedListener(params);
  }

  ngOnDestroy(): void {
    if (this.columnResizedListener) {
      this.columnResizedListener();
      this.columnResizedListener = null;
    }
  }

  override refresh(): boolean {
    return false;
  }

  getChipClass = (chip: any): string => {
    return CommonUtils.getObjectNestedProperty(this.params()["classParam"], chip);
  };

  getChipText = (chip: any): string => {
    return typeof chip === "string"
      ? chip
      : CommonUtils.getObjectNestedProperty(this.params()["textParam"], chip);
  };

  getChipLink = (chip: any): any => CellRendererUtils.getLink(this.params(), chip);

  private initializeParams(params: ICellRendererParams): void {
    let value = params.value;

    value = !!value && typeof value === "object" && !Array.isArray(value) ? [value] : value; // If single chip, put in an array
    if (Array.isArray(value)) {
      value = value.filter((item) => item != null);
    }
    this.params.set({ ...params, value });
    this.originalValue.set(value);

    this.hasLink = !!(
      this.params()["linkRoute"] ||
      this.params()["linkRouteIdParam"] ||
      this.params()["linkRouteFn"]
    );

    this.cellContainerClass.set(
      `${this.DEFAULT_CELL_CONTAINER_CLASS} ${this.params()["cellContainerClass"]}`,
    );
  }

  private setupColumnResizedListener(params: ICellRendererParams): void {
    const { api } = params;

    api.addEventListener("columnResized", this.onColumnResized.bind(this));
    api.addEventListener("columnPinned", this.onColumnPinned.bind(this));
    api.addEventListener("columnVisible", this.onColumnVisible.bind(this));
    api.addEventListener("bodyScrollEnd", this.onBodyScrollEnd.bind(this));
    api.addEventListener("cellValueChanged", this.onCellValueChanged.bind(this));

    this.columnResizedListener = () => {
      api.removeEventListener("columnResized", this.onColumnResized.bind(this));
      api.removeEventListener("columnPinned", this.onColumnPinned.bind(this));
      api.removeEventListener("columnVisible", this.onColumnVisible.bind(this));
      api.removeEventListener("bodyScrollEnd", this.onBodyScrollEnd.bind(this));
      api.removeEventListener("cellValueChanged", this.onCellValueChanged.bind(this));
    };
  }

  private onColumnResized(event: ColumnResizedEvent): void {
    if (!event.finished) {
      return;
    }

    const column = this.findRelevantColumn(event.columns);

    if (!column) {
      return;
    }

    this.processColumnResize(column);
  }

  private onColumnVisible(event: ColumnVisibleEvent): void {
    const eventColumns = event.column ? [event.column] : event.columns;

    const column = this.findRelevantColumn(eventColumns);

    if (!column || !event.visible) {
      return;
    }

    this.forceColumnResizeEvent(column);
  }

  private onColumnPinned(event: ColumnPinnedEvent): void {
    this.forceColumnResizeEvent(event.column);
  }

  private onBodyScrollEnd(): void {
    this.processColumnResize(this.params().column);
  }

  private onCellValueChanged(event: CellValueChangedEvent): void {
    this.forceColumnResizeEvent(event.column);
  }

  private forceColumnResizeEvent(column: Column): void {
    this.params().api.setColumnWidths([
      {
        key: column.getColId(),
        newWidth: this.cellContainer()?.nativeElement?.offsetWidth,
      },
    ]);
  }

  private processColumnResize(column: Column): void {
    const actualWidth = column.getActualWidth();
    const width = this.cellContainer()?.nativeElement?.offsetWidth;

    if (!width) {
      return;
    }

    this.handleFirstRender();
    this.adjustChipsVisibility(actualWidth);
  }

  private findRelevantColumn(columns: Column[]) {
    for (const column of columns) {
      if (column.getColId() === this.params().column.getColId()) {
        return column;
      }
    }

    return null;
  }

  private handleFirstRender(): void {
    if (!this.isFirstRender) {
      return;
    }

    const chips = this.cellContainer().nativeElement.querySelectorAll(
      `.${this.tagClass}`,
    ) as NodeListOf<HTMLElement>;

    this.chipBounds.set(
      Array.from(chips).map((chip) => {
        const rect = chip.getBoundingClientRect();

        return { width: rect.width, left: rect.left, right: rect.right };
      }),
    );

    if (this.chipBounds().length > 1) {
      this.chipsGap.set(this.chipBounds()[1].left - this.chipBounds()[0].right);
    }

    const parent = this.cellContainer().nativeElement.closest(".ag-cell-value");

    this.xOffsetBetweenCellContainerAndParent.set(
      this.cellContainer().nativeElement.getBoundingClientRect().x -
        parent.getBoundingClientRect().x,
    );

    this.originalCellContainerWidth.set(
      this.cellContainer().nativeElement.offsetWidth + this.xOffsetBetweenCellContainerAndParent(),
    );

    this.isFirstRender = false;
  }

  private adjustChipsVisibility(actualWidth: number): void {
    if (this.originalCellContainerWidth() >= actualWidth) {
      const chipsToShow = this.calculateVisibleChips(actualWidth);

      this.params.set({ ...this.params(), value: this.originalValue().slice(0, chipsToShow) });
    } else {
      this.params.set({ ...this.params(), value: this.originalValue() });
    }
  }

  private calculateVisibleChips(actualWidth: number): number {
    let currentX = this.xOffsetBetweenCellContainerAndParent();
    const hiddenChipsBadgeOffset = 50;

    let chipsToShow = 0;

    for (let i = 0; i < this.chipBounds().length; i++) {
      const chipBound = this.chipBounds()[i];

      if (currentX + chipBound.width + hiddenChipsBadgeOffset > actualWidth) {
        break;
      }

      chipsToShow++;
      currentX += chipBound.width + this.chipsGap();
    }

    return chipsToShow;
  }
}
